import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  computed,
  input,
  signal
} from '@angular/core';
import type { ToolCallState } from '@portal/app/shared/components/ai-assistant-panel/types/chat-block.types';
import {
  formatArgs,
  formatResult,
  humanizeToolName
} from '@portal/app/shared/components/ai-assistant-panel/tools/tool-formatters';

/**
 * One tool invocation, modeled on Fleet's compact reasoning UI:
 *
 *   ▸ Read File          collapsed
 *   ⟳ Read File          pending (animated spinner)
 *   ⚠ Read File          error
 *   ▾ Read File          expanded (ARGUMENTS + RESULT below)
 *
 * State is conveyed by the single leading icon. Animation uses
 * Tailwind's built-in `animate-spin`.
 */
@Component({
  selector: 'portal-tool-call',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tool-call.component.html'
})
export class PortalToolCallComponent {
  @HostBinding('class') readonly hostClass = 'block';

  readonly name = input.required<string>();
  readonly args = input<unknown>(undefined);
  readonly result = input<unknown>(undefined);
  readonly state = input<ToolCallState>('pending');

  protected readonly _expanded = signal(false);

  protected readonly displayName = computed(() =>
    humanizeToolName(this.name())
  );

  protected readonly headerIcon = computed(() => {
    switch (this.state()) {
      case 'pending':
        return 'progress_activity';
      case 'error':
        return 'error';
      default:
        return 'chevron_right';
    }
  });

  protected readonly formattedArgs = computed(() => formatArgs(this.args()));
  protected readonly formattedResult = computed(() =>
    formatResult(this.result())
  );

  protected toggle(): void {
    this._expanded.update((v) => !v);
  }
}
